// frontend/src/App.js
import { useCallback, useEffect, useRef, useState } from "react";
import AboutSummary from "./components/AboutSummary";
import DomainForm from "./components/DomainForm";
import ErrorBoundary from "./components/ErrorBoundary";
import Feedback from "./components/Feedback";
import ScanProgress from "./components/ScanProgress";
import ScanResults from "./components/ScanResults";
import ToastHost from "./components/ToastHost";
import WhatWeCheck from "./components/WhatWeCheck";
import { ApiError, isValidDomain, normalizeDomain, scanDomain } from "./lib/api";
import "./App.css";

const RECENT_KEY = "cybercheck:recent";
const RECENT_MAX = 5;

function readRecent() {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

export default function App() {
  const [phase, setPhase] = useState("idle");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [recent, setRecent] = useState([]);

  const abortRef = useRef(null);
  const lastScannedRef = useRef(null);

  useEffect(() => {
    setRecent(readRecent());
  }, []);

  useEffect(() => () => abortRef.current?.abort(), []);

  const remember = useCallback((domain) => {
    setRecent((prev) => {
      const next = [domain, ...prev.filter((d) => d !== domain)].slice(0, RECENT_MAX);
      try {
        localStorage.setItem(RECENT_KEY, JSON.stringify(next));
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);

  const runScan = useCallback(
    async (rawDomain) => {
      const domain = normalizeDomain(rawDomain);
      if (!isValidDomain(domain)) {
        setError("That doesn't look like a valid domain. Try something like example.co.za");
        setPhase("idle");
        return;
      }

      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      setError("");
      setResult(null);
      setPhase("scanning");
      lastScannedRef.current = domain;

      try {
        const url = new URL(window.location.href);
        url.searchParams.set("domain", domain);
        window.history.replaceState({}, "", url);
      } catch {
        /* ignore */
      }

      try {
        const data = await scanDomain(domain, { signal: controller.signal });
        if (controller.signal.aborted) return;
        setResult(data);
        setPhase("done");
        remember(domain);
      } catch (err) {
        if (err.name === "AbortError") return;
        if (err instanceof ApiError) {
          if (err.status === 429) {
            setError(
              err.retryAfter
                ? `Too many scans. Try again in ${err.retryAfter}s.`
                : "Too many scans. Please slow down."
            );
          } else if (err.status === 400) {
            setError(err.message || "That domain wasn't accepted by the scanner.");
          } else if (err.status === 504) {
            setError("The scan timed out. The target may be slow or unreachable.");
          } else if (err.status >= 500) {
            setError("The scanner had a problem. Please try again in a moment.");
          } else {
            setError(err.message || "The scan failed. Please try again.");
          }
        } else {
          setError("The scan failed. Please check your connection and try again.");
        }
        setPhase("idle");
      } finally {
        if (abortRef.current === controller) abortRef.current = null;
      }
    },
    [remember]
  );

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const d = params.get("domain");
    if (d && d !== lastScannedRef.current) runScan(d);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cancel = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setPhase("idle");
    setError("");
  }, []);

  function reset() {
    cancel();
    setResult(null);
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete("domain");
      window.history.replaceState({}, "", url);
    } catch {
      /* ignore */
    }
  }

  return (
    <div className="relative min-h-screen overflow-x-hidden text-gray-900">
      {/* Off-white page with a warm sun glow top-left */}
      <div className="cc-bg" aria-hidden="true" />

      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded-lg focus:bg-gray-900 focus:px-3 focus:py-2 focus:text-sm focus:text-white focus:shadow-lg"
      >
        Skip to content
      </a>

      <header className="sticky top-0 z-30 border-b border-gray-200/70 bg-white/70 backdrop-blur-md">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-5 py-3.5">
          <button
            type="button"
            onClick={reset}
            className="group flex items-center gap-2.5 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-2"
          >
            <span className="grid h-10 w-10 place-items-center rounded-2xl bg-gradient-to-br from-amber-400 via-orange-500 to-amber-600 text-sm font-bold text-white shadow-md shadow-amber-500/25 transition group-hover:shadow-lg">
              CC
            </span>
            <span>
              <span className="block text-sm font-semibold leading-tight tracking-tight text-gray-900">
                CyberCheck
              </span>
              <span className="block text-[11px] leading-tight text-gray-500">
                Know your website. Protect your business.
              </span>
            </span>
          </button>

          <span className="hidden items-center gap-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2.5 py-1 text-[11px] font-medium text-emerald-700 sm:inline-flex">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-500" />
            Non-invasive · POPIA-aware
          </span>
        </div>
      </header>

      {/* AI summary pill — top-left, just below the header */}
      <div className="mx-auto max-w-5xl px-5 pt-4">
        <AboutSummary />
      </div>

      <main id="main" className="relative mx-auto max-w-5xl px-5 py-10">
        <ErrorBoundary>
          {phase !== "done" && (
            <section className="mb-10 text-center cc-fade-up">
              <span className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-white/80 px-3 py-1 text-[11px] font-medium text-gray-600 shadow-sm backdrop-blur">
                <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                Free · No signup · South African
              </span>
              <h1 className="mt-4 text-3xl font-semibold leading-tight tracking-tight text-gray-900 sm:text-5xl">
                Is your website{" "}
                <span className="bg-gradient-to-r from-amber-500 via-orange-500 to-amber-600 bg-clip-text text-transparent">
                  actually secure?
                </span>
              </h1>
              <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-gray-600 sm:text-base">
                Run a free, non-invasive external scan. Get prioritised findings
                written in plain English — plus the exact questions to ask your
                developer or hosting provider.
              </p>
            </section>
          )}

          <DomainForm
            onSubmit={runScan}
            busy={phase === "scanning"}
            recent={recent}
            onPick={runScan}
            onCancel={cancel}
          />

          {error && (
            <p
              role="alert"
              className="mx-auto mt-4 max-w-2xl rounded-2xl border border-red-200 bg-red-50/90 px-4 py-3 text-sm text-red-700 shadow-sm cc-pop"
            >
              {error}
            </p>
          )}

          {phase === "scanning" && lastScannedRef.current && (
            <ScanProgress domain={lastScannedRef.current} />
          )}

          {phase === "done" && result && (
            <ScanResults result={result} onRescan={() => runScan(result.domain)} />
          )}

          {phase !== "scanning" && (
            <section className="mt-16 grid gap-3 sm:grid-cols-3">
              {[
                {
                  t: "Non-invasive",
                  d: "External checks only. We never log in, exploit, or change anything.",
                  ring: "ring-emerald-500/20",
                  dot: "bg-emerald-500",
                  accent: "text-emerald-700",
                },
                {
                  t: "Plain English",
                  d: "Every finding explains what it means, why it matters, and what to fix first.",
                  ring: "ring-amber-500/20",
                  dot: "bg-amber-500",
                  accent: "text-amber-700",
                },
                {
                  t: "POPIA-aware",
                  d: "Security-readiness indicators — not legal advice or certification.",
                  ring: "ring-orange-500/20",
                  dot: "bg-orange-500",
                  accent: "text-orange-700",
                },
              ].map((c, i) => (
                <div
                  key={c.t}
                  className={`cc-card rounded-2xl p-5 shadow-sm ring-1 ${c.ring}`}
                  style={{ animationDelay: `${i * 80}ms` }}
                >
                  <div className="flex items-center gap-2">
                    <span className={`h-1.5 w-1.5 rounded-full ${c.dot}`} />
                    <p className={`text-sm font-semibold tracking-tight ${c.accent}`}>
                      {c.t}
                    </p>
                  </div>
                  <p className="mt-2 text-xs leading-relaxed text-gray-600">{c.d}</p>
                </div>
              ))}
            </section>
          )}

          {phase !== "scanning" && <WhatWeCheck />}

          <Feedback />
        </ErrorBoundary>
      </main>

      <footer className="mt-16 border-t border-gray-200/70 bg-white/60 backdrop-blur">
        <div className="mx-auto flex max-w-5xl flex-col items-start gap-3 px-5 py-6 text-xs leading-relaxed text-gray-500 sm:flex-row sm:items-center sm:justify-between">
          <p className="max-w-2xl">
            CyberCheck provides security-readiness indicators only. It is not a
            penetration test, a security guarantee, or legal POPIA certification.
          </p>
          <p className="text-gray-400">Built by Bytes4Bytes</p>
        </div>
      </footer>

      <ToastHost />
    </div>
  );
}